import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import classNames from 'classnames';
import { msgid, ngettext, t } from 'ttag';

import PointerIcon from '@svg/pointer.svg';
import BagsIcon from '@svg/bags.svg';
import StopwatchIcon from '@svg/stopwatch.svg';
import TimeIcon from '@svg/time.svg';
import { GenderEnum } from '@models';
import { useAppContext } from '@contexts/AppContext';
import { publicMemberProfile } from '@urls';
import { Link } from '@ui/Link';
import { Button } from '@ui/Button';
import { Image } from '@ui/Image';
import { Stars } from '@ui/Stars';
import { TopSellerBadge } from '@components/TopSellerBadge';
import { SellerRatingModal } from '@components/SellerRatingModal';

import s from './ProductDetailsSellerInfo.sss';

interface Seller {
  id: number,
  thumbnail: string,
  shopName?: string | null,
  username: string,
  gender?: GenderEnum | null,
  orderRate?: string | null,
  isTopSeller: boolean,
  hasRecentActivity: boolean,
  middleResponseTime?: string | null,
  city?: {
    id: number,
    name: string,
  } | null,
  sellerRatingInfo?: {
    overallRating: string,
    reviewsCount: number,
  } | null
}

interface Props {
  className?: string,
  seller: Seller,
  isAuth: boolean,
  isSubscribed: boolean,
  isSubscriptionLoading: boolean,
  onToggleSubscription: () => void,
}

export const ProductDetailsSellerInfo: React.FC<Props> = ({
  className,
  seller,
  isAuth,
  isSubscribed,
  isSubscriptionLoading,
  onToggleSubscription,
}) => {
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);

  const { isOutlet } = useAppContext();

  const sellerDisplayName = seller.shopName || seller.username;
  const sellerOverallRating = seller.sellerRatingInfo?.overallRating || 0;
  const sellerReviewsCount = seller.sellerRatingInfo?.reviewsCount || 0;
  const orderRate = seller.orderRate;
  const sellerProfilePathname = generatePath(publicMemberProfile, { username: seller.username });

  const handleSellerClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isOutlet) {
      e.preventDefault();
    }
  };

  const handleRatingClick = () => {
    if (!isOutlet) {
      setIsRatingModalOpen(true);
    }
  };

  return (
    <>
      <div className={classNames(s.root, className)}>
        <p className={s.title}>
          {t`Продавець`}
        </p>

        <div className={s.seller}>
          <Link
            className={s.link}
            theme='black'
            to={sellerProfilePathname}
            onClick={handleSellerClick}
          >
            <Image
              className={s.avatar}
              src={seller.thumbnail}
              width='40'
              height='40'
              alt={sellerDisplayName}
            />
          </Link>

          <Link
            className={s.name}
            theme='black'
            to={sellerProfilePathname}
            onClick={handleSellerClick}
          >
            {sellerDisplayName}
          </Link>

          <Button
            className={s.ratingButton}
            theme='text'
            onClick={handleRatingClick}
          >
            <Stars
              value={Number(sellerOverallRating)}
              readOnly={true}
              gap={4}
            />

            <p className={s.reviews}>
              {sellerOverallRating}
              &nbsp;
              <span className={s.reviewsCount}>
                {ngettext(
                  msgid`(${sellerReviewsCount} оцінка)`,
                  `(${sellerReviewsCount} оцінки)`,
                  `(${sellerReviewsCount} оцінок)`,
                  sellerReviewsCount,
                )}
              </span>
            </p>
          </Button>

          <ul className={s.infoList}>
            {seller.isTopSeller && (
              <li className={s.infoItem}>
                <TopSellerBadge className={s.topSellerBadge} />
              </li>
            )}

            {!!seller.city && (
              <li className={s.infoItem}>
                <PointerIcon
                  className={s.infoIcon}
                  width='16'
                  height='16'
                />

                {seller.city?.name}
              </li>
            )}

            {!!seller.orderRate && (
              <li className={s.infoItem}>
                <BagsIcon
                  className={s.infoIcon}
                  width='16'
                  height='16'
                />

                {t`${orderRate}% виконаних замовлень`}
              </li>
            )}

            {!seller.hasRecentActivity && (
              <li className={s.infoItem}>
                <StopwatchIcon
                  className={s.infoIcon}
                  width='16'
                  height='16'
                />

                {seller.gender === GenderEnum.GenderMale ? t`був` : t`була`}
                &nbsp;
                {t`На Шафі більше 30 днів тому`}
              </li>
            )}

            {!!seller.middleResponseTime && (
              <li className={s.infoItem}>
                <TimeIcon
                  className={s.infoIcon}
                  width='16'
                  height='16'
                />

                {seller.middleResponseTime}
              </li>
            )}
          </ul>

          {isAuth && !isOutlet && (
            <Button
              className={s.followButton}
              theme={'secondary'}
              disabled={isSubscriptionLoading}
              onClick={onToggleSubscription}
            >
              {isSubscribed ? t`Ви підписані` : t`Підписатися`}
            </Button>
          )}
        </div>
      </div>

      <SellerRatingModal
        sellerId={seller.id}
        isOpen={isRatingModalOpen}
        onRequestClose={() => setIsRatingModalOpen(false)}
      />
    </>
  );
};
